import React, { useEffect, useRef } from 'react';

/**
 * StandardizedAlertModal - A reusable modal component for alerts, warnings, errors, and confirmations
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls visibility of the modal
 * @param {function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message content
 * @param {string} props.type - Type of alert: 'error', 'warning', 'info', 'success', 'confirm'
 * @param {function} props.onConfirm - Optional callback for confirm action (used with type='confirm')
 * @param {string} props.confirmText - Text for confirm button (default: 'OK')
 * @param {string} props.cancelText - Text for cancel button (default: 'Cancel')
 */
const AlertModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  type = 'info',
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel'
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement;
      
      // Focus the modal
      modalRef.current?.focus();

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Restore focus to previous element
      previousActiveElement.current?.focus();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      case 'confirm':
        return '❓';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'error':
        return 'var(--color-error)';
      case 'warning':
        return 'var(--color-warning)';
      case 'success':
        return 'var(--color-success)';
      case 'info':
      default:
        return 'var(--color-info)';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="alert-modal-title"
      aria-describedby="alert-modal-description"
    >
      <div 
        className="modal-content"
        ref={modalRef}
        tabIndex={-1}
        style={{
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <div 
          style={{
            fontSize: '48px',
            marginBottom: 'var(--spacing-md)',
            color: getIconColor()
          }}
          aria-hidden="true"
        >
          {getIcon()}
        </div>

        {/* Title */}
        {title && (
          <h2 
            id="alert-modal-title"
            style={{
              marginTop: 0,
              marginBottom: 'var(--spacing-md)',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-primary)'
            }}
          >
            {title}
          </h2>
        )}

        {/* Message */}
        <p 
          id="alert-modal-description"
          style={{
            marginBottom: 'var(--spacing-lg)',
            fontSize: 'var(--font-size-base)',
            color: 'var(--color-text-primary)',
            lineHeight: 'var(--line-height-relaxed)'
          }}
        >
          {message}
        </p>

        {/* Actions */}
        <div 
          style={{
            display: 'flex',
            gap: 'var(--spacing-md)',
            justifyContent: 'center',
            marginTop: 'var(--spacing-lg)'
          }}
        >
          {type === 'confirm' ? (
            <>
              <button
                onClick={onClose}
                className="btn"
                style={{
                  padding: '12px 24px',
                  fontSize: 'var(--font-size-base)',
                  backgroundColor: 'var(--color-disabled)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                className="btn btn-success"
                style={{
                  padding: '12px 24px',
                  fontSize: 'var(--font-size-base)',
                  backgroundColor: 'var(--color-success)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
                autoFocus
              >
                {confirmText}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="btn btn-success"
              style={{
                padding: '12px 24px',
                fontSize: 'var(--font-size-base)',
                backgroundColor: 'var(--color-success)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontWeight: 'bold',
                minWidth: '100px'
              }}
              autoFocus
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
